import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, repeatsAClass } from '../skillUsage';

export const radialSurroundSkill: CodeoSkill = {
    name: SKILL_NAMES.radialSurround,
    kind: 'codeo',
    description:
        'Arrange items evenly around a centre and bring them in as a ring — for a set that ' +
        'surrounds, orbits, or converges on one thing, where no member ranks above another.',
    documentUsesSkill: check(
        (script, markup) =>
            repeatsAClass(markup) &&
            /offsetLeft|offsetTop|Math\.cos|Math\.sin|rotate\(/.test(script),
    ),
    instructions: `# Radial Surround

Items are placed on a circle around a centre and arrive together as a ring, either converging inward or expanding outward. Unlike a row or a grid, a ring has no first and no last — everything is equidistant from the middle, which is what it says: these all relate to that, equally.

Use it for a set with no ranking. If one member matters more, a ring is the wrong shape and \`${SKILL_NAMES.hubAndSpokes}\` or \`${SKILL_NAMES.spotlightFocusCard}\` will serve better.

## Markup

Compute the positions at author time — \`cx + r·cos(θ)\`, \`cy + r·sin(θ)\` — and write the resulting numbers in. Centre each item with negative margins, never a CSS transform, since they are scale-tweened.

\`\`\`html
<div id="ring-stage" style="position: absolute; left: 560px; top: 140px; width: 800px; height: 800px;">
  <div id="ring-core" style="position: absolute; left: 400px; top: 400px; margin-left: -150px; margin-top: -150px; width: 300px; height: 300px; border-radius: 50%; display: flex; align-items: center; justify-content: center; background: var(--l5-color-2, #38bdf8); font-family: var(--l5-font-0), sans-serif; font-size: 46px; font-weight: 800; color: var(--l5-color-1, #101820);">You</div>
  <div class="orbiter" style="position: absolute; left: 400px; top: 90px; margin-left: -95px; margin-top: -44px; width: 190px; height: 88px; display: flex; align-items: center; justify-content: center; border-radius: 16px; background: var(--l5-color-1, #101820); font-family: var(--l5-font-0), sans-serif; font-size: 30px; font-weight: 700; color: var(--l5-color-0, #ffffff);">Docs</div>
  <div class="orbiter" style="position: absolute; left: 669px; top: 245px; margin-left: -95px; margin-top: -44px; width: 190px; height: 88px; display: flex; align-items: center; justify-content: center; border-radius: 16px; background: var(--l5-color-1, #101820); font-family: var(--l5-font-0), sans-serif; font-size: 30px; font-weight: 700; color: var(--l5-color-0, #ffffff);">Chat</div>
  <!-- remaining orbiters at 30 degree increments -->
</div>
\`\`\`

## Timeline code

Bring the ring in from slightly outside its final radius so it reads as converging rather than fading up in place.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const orbiters = gsap.utils.toArray('.orbiter');
const CENTRE_X = 400;
const CENTRE_Y = 400;

gsap.set('#ring-core', { scale: 0.6, opacity: 0 });

// Each orbiter starts pushed out along its own radius, so the ring closes inward.
orbiters.forEach((orbiter) => {
    const dx = orbiter.offsetLeft + orbiter.offsetWidth / 2 - CENTRE_X;
    const dy = orbiter.offsetTop + orbiter.offsetHeight / 2 - CENTRE_Y;
    gsap.set(orbiter, { x: dx * 0.35, y: dy * 0.35, opacity: 0, scale: 0.9 });
});

tl.to('#ring-core', { scale: 1, opacity: 1, duration: 0.5, ease: 'back.out(1.4)' }, 0.2);
tl.to(orbiters, {
    x: 0,
    y: 0,
    opacity: 1,
    scale: 1,
    duration: 0.7,
    ease: 'power3.out',
    stagger: { each: 0.06, from: 'start' },
}, 0.5);

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Direction**: converging inward (above) reads as gathering. Starting *inside* and expanding outward reads as radiating from the centre — better for distribution or reach.
- **Stagger origin**: \`from: 'start'\` sweeps round the ring like a clock hand. Setting all offsets to zero brings the whole ring in at once, which is more monumental and less playful.
- **Slow rotation**: a fraction of a turn on the stage across the scene keeps the ring alive after it lands. Keep it under about 6° or it reads as spinning.
- **Connectors**: adding lines from centre to each item turns this into \`${SKILL_NAMES.hubAndSpokes}\`, which makes a structural claim rather than a relational one.
- **Uneven radius**: pushing two items further out breaks the mechanical perfection and reads as designed rather than generated.
- **Count**: 5–8 items. Fewer and the ring reads as accidental; more and the labels collide.

## Pitfalls

- Items are \`x\`/\`y\`/\`scale\`-tweened, so none may carry a CSS \`transform\`. Centre them with negative margins.
- Compute the ring positions once, at author time, and write literal numbers into the markup — don't generate them at runtime unless the count is genuinely dynamic.
- \`offsetLeft\`/\`offsetTop\` are read at build time here, which is correct because the layout is static.
- A ring of equal boxes is close to the "identical card grid" the style guidance warns against. Vary the item sizes, or let the centre carry all the weight.
- Text inside a small orbiter has to clear the readable floor — one or two words at 28px+, never a phrase.
- Don't rotate the items themselves to face the centre. Rotated labels are unreadable at video pace.
- Once the ring lands the frame is still; give the stage a drift — see \`${SKILL_NAMES.driftAndHold}\`.`,
};
